import { CommonModule } from '@angular/common';
import { Component, computed, Inject, Signal, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TemplateVariable } from '../../../_interfaces/email.interface';
import { TestEmailDialogComponent } from '../../../shared/test-email-dialog/test-email-dialog.component';
import { getLanguageName, isRtlLanguage } from '../../../_datas/languages.data';
import { sortLanguageCodes } from '../../../_utils/utils';

interface LanguageOption {
  languageCode: string;
  languageName: string;
  isValid: boolean;
  hasContent: boolean;
}

export interface EmailPreviewData {
  templateName: string;
  htmlContent?: string; // For backward compatibility
  subject?: string; // For backward compatibility
  htmlPart?: { [langCode: string]: string }; // Multi-language HTML
  subjectPart?: { [langCode: string]: string }; // Multi-language subject
  templateVariables?: TemplateVariable[];
  defaultLangCode?: string;
  languageCodes?: string[]; // Available languages
  isPublishedVersion?: boolean; // Flag to indicate this is the published snapshot
  publishedAt?: Date; // Publication timestamp
  templateNotFound?: boolean;
  // Test email data
  languages?: LanguageOption[]; // Language options with validation states for test email
  defaultEmail?: string; // Default email for test email dialog
  // Source determines how test email is sent:
  // 'editor' = Always use raw HTML content (for testing drafts/unpublished templates)
  // 'published' = Always use AWS SES template (for testing production templates)
  source?: 'editor' | 'published';
}

@Component({
  selector: 'app-email-template-preview',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './email-template-preview.component.html',
  styleUrl: './email-template-preview.component.scss'
})

export class EmailTemplatePreviewComponent {

  selectedLang = signal<string>(this.data.defaultLangCode || 'en');
  isDarkMode = signal(false);
  viewMode = signal<'desktop' | 'mobile'>('desktop');
  showVariableValues = signal(false);
  templateNotFound = signal(false);

  // Fullscreen mode
  isFullscreen = signal(false);
  customWidth = signal(800);
  customHeight = signal(600);

  languageCodes: Signal<string[]> = computed(() => {
    return sortLanguageCodes(this.data.languageCodes || [this.data.defaultLangCode || 'en']);
  });

  templateName: Signal<string> = computed(() => this.data.templateName);

  /** Check if the current content is empty (no HTML content for the selected language) */
  hasNoContent: Signal<boolean> = computed(() => {
    if (this.data.htmlPart) {
      const content = this.data.htmlPart[this.selectedLang()] || '';
      return !content.trim();
    }
    return !this.data.htmlContent?.trim();
  });

  subject: Signal<string> = computed(() => {
    let subjectText: string;
    // Support both new multi-language format and legacy single-language format
    if (this.data.subjectPart) {
      subjectText = this.data.subjectPart[this.selectedLang()] || '';
    } else {
      subjectText = this.data.subject || '';
    }
    if (this.showVariableValues() && this.data.templateVariables?.length) {
      subjectText = this.replaceVariables(subjectText);
    }
    return subjectText || (this.hasNoContent() ? '' : 'No Subject');
  });

  htmlContent: Signal<SafeHtml> = computed(() => {
    return this.sanitizer.bypassSecurityTrustHtml(this.rawHtmlContent());
  });

  // Safe HTML for iframe srcdoc - bypasses Angular sanitizer to preserve <style> tags
  safeHtmlForIframe: Signal<SafeHtml> = computed(() => {
    return this.sanitizer.bypassSecurityTrustHtml(this.rawHtmlContent());
  });

  rawHtmlContent: Signal<string> = computed(() => {
    let content: string;
    // Support both new multi-language format and legacy single-language format
    if (this.data.htmlPart) {
      content = this.data.htmlPart[this.selectedLang()] || '';
    } else {
      content = this.data.htmlContent || '';
    }

    // Replace variables with values if toggle is on
    if (this.showVariableValues() && this.data.templateVariables?.length) {
      content = this.replaceVariables(content);
    }

    // Process content for proper light/dark mode handling
    const isDark = this.isDarkMode();
    return this.processEmailForColorScheme(content, isDark, this.selectedLang());
  });

  /**
   * Process email HTML to simulate Gmail's dark mode rendering
   *
   * Gmail uses "partial color inversion":
   * - Light backgrounds (#ffffff, #f4f7fa, etc.) are inverted to dark
   * - Dark text (#000000, #333333, etc.) is inverted to light
   * - Already dark backgrounds and light text are generally preserved
   *
   * This simulates how Gmail Android/iOS renders emails in dark mode.
   */
  private processEmailForColorScheme(html: string, isDark: boolean, langCode?: string): string {
    const hasDoctype = html.trim().toLowerCase().startsWith('<!doctype');

    const rtlAlignmentStyles = langCode && isRtlLanguage(langCode) ? `
<style id="rtl-alignment-fix">
  [dir="rtl"] h1, [dir="rtl"] h2, [dir="rtl"] h3, [dir="rtl"] h4, [dir="rtl"] h5, [dir="rtl"] h6 {
    text-align: center !important;
  }
  [dir="rtl"] .footer, [dir="rtl"] [class*="footer"],
  [dir="rtl"] .footer td, [dir="rtl"] [class*="footer"] td,
  [dir="rtl"] .footer p, [dir="rtl"] [class*="footer"] p {
    text-align: center !important;
  }
</style>` : '';

    if (isDark) {
      // Gmail-style dark mode: Invert light colors to dark, dark colors to light
      // This simulates Gmail's partial color inversion behavior

      const viewportMeta = '<meta name="viewport" content="width=device-width, initial-scale=1">';
      const gmailDarkModeStyles = `
${viewportMeta}
<style id="gmail-dark-mode-simulation">
  /*
   * Gmail Dark Mode Simulation
   * Simulates Gmail's partial color inversion behavior where:
   * - Light backgrounds become dark (#121212)
   * - Dark text becomes light (#e8eaed)
   * - Links get Gmail's blue color (#8ab4f8)
   */

  /* Base dark mode settings */
  :root {
    color-scheme: dark !important;
  }

  /* Gmail inverts the html/body background */
  html, body {
    background-color: #121212 !important;
    color: #e8eaed !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  table { max-width: 100% !important; }
  body, td, th, p, div { word-wrap: break-word !important; overflow-wrap: break-word !important; }

  /* Gmail inverts common light background colors to dark */
  [style*="background-color: #fff"],
  [style*="background-color:#fff"],
  [style*="background-color: white"],
  [style*="background-color:white"],
  [style*="background: #fff"],
  [style*="background:#fff"],
  [bgcolor="#ffffff"],
  [bgcolor="#FFFFFF"],
  [bgcolor="white"] {
    background-color: #121212 !important;
  }

  /* Gmail inverts light gray backgrounds */
  [style*="background-color: #f"],
  [style*="background-color:#f"],
  [bgcolor^="#f"],
  [bgcolor^="#F"] {
    background-color: #1e1e1e !important;
  }

  /* Gmail inverts dark text colors to light */
  [style*="color: #000"],
  [style*="color:#000"],
  [style*="color: black"],
  [style*="color:black"],
  [style*="color: #333"],
  [style*="color:#333"],
  [style*="color: #222"],
  [style*="color:#222"] {
    color: #e8eaed !important;
  }

  /* Gmail's link color in dark mode */
  a {
    color: #8ab4f8 !important;
  }

  /* Table cells and divs with light backgrounds */
  td, th, div, table {
    /* Let inline styles override, but set defaults for elements without explicit colors */
  }

  /* Force invert common email container patterns */
  .email-container,
  .wrapper,
  .container,
  .content,
  .body-content,
  [class*="container"],
  [class*="wrapper"],
  [class*="content"] {
    background-color: #1e1e1e !important;
    color: #e8eaed !important;
  }

  /* Preserve header gradient backgrounds but adjust text */
  .header h1,
  .header h2,
  .header p,
  .header span {
    color: #ffffff !important;
  }

  /* Footer text typically lighter gray */
  .footer,
  [class*="footer"] {
    background-color: #121212 !important;
    color: #9aa0a6 !important;
  }

  /* Gmail inverts paragraph and heading text */
  p, h1, h2, h3, h4, h5, h6, span, li, td {
    color: #e8eaed !important;
  }

  /* Preserve button styling but ensure readable text */
  .btn, .button, [class*="button"], [class*="btn"] {
    color: #ffffff !important;
  }

  /* Images should not be inverted */
  img {
    /* Gmail doesn't invert images */
  }
</style>`;

      const dirAttr = langCode && isRtlLanguage(langCode) ? ' dir="rtl"' : '';

      if (hasDoctype) {
        let result = html.replace(/<head[^>]*>/i, `$&${gmailDarkModeStyles}${rtlAlignmentStyles}`);
        if (dirAttr && !/<html[^>]*dir=/i.test(result)) {
          result = result.replace(/<html([^>]*)>/i, `<html$1${dirAttr}>`);
        }
        return result;
      } else {
        return `<!DOCTYPE html>
<html${dirAttr}>
<head>
  <meta name="color-scheme" content="dark">
  ${gmailDarkModeStyles}
  ${rtlAlignmentStyles}
</head>
<body>${html}</body>
</html>`;
      }
    } else {
      // Light mode: Remove dark mode media queries and ensure proper document structure
      let processedHtml = html;

      // Remove @media (prefers-color-scheme: dark) blocks for light mode preview
      processedHtml = processedHtml.replace(
        /@media\s*\(\s*prefers-color-scheme\s*:\s*dark\s*\)\s*\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/gi,
        ''
      );

      // Replace color-scheme meta tags
      processedHtml = processedHtml.replace(
        /<meta\s+name=["']color-scheme["']\s+content=["'][^"']*dark[^"']*["']\s*\/?>/gi,
        '<meta name="color-scheme" content="light only">'
      );
      processedHtml = processedHtml.replace(
        /<meta\s+name=["']supported-color-schemes["']\s+content=["'][^"']*dark[^"']*["']\s*\/?>/gi,
        '<meta name="supported-color-schemes" content="light only">'
      );

      // Light mode: only set color-scheme, don't force background colors
      const viewportMeta = '<meta name="viewport" content="width=device-width, initial-scale=1">';
      const lightModeStyles = `
${viewportMeta}
<style id="light-mode-override">
  :root {
    color-scheme: light only !important;
  }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
  }
  table { max-width: 100% !important; }
  body, td, th, p, div { word-wrap: break-word !important; overflow-wrap: break-word !important; }
</style>`;

      const dirAttr = langCode && isRtlLanguage(langCode) ? ' dir="rtl"' : '';

      if (hasDoctype) {
        let result = processedHtml.replace(/<head[^>]*>/i, `$&${lightModeStyles}${rtlAlignmentStyles}`);
        if (dirAttr && !/<html[^>]*dir=/i.test(result)) {
          result = result.replace(/<html([^>]*)>/i, `<html$1${dirAttr}>`);
        }
        return result;
      } else {
        return `<!DOCTYPE html>
<html${dirAttr}>
<head>
  <meta name="color-scheme" content="light only">
  ${lightModeStyles}
  ${rtlAlignmentStyles}
</head>
<body>${processedHtml}</body>
</html>`;
      }
    }
  }

  constructor(
    public dialogRef: MatDialogRef<EmailTemplatePreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EmailPreviewData,
    private sanitizer: DomSanitizer,
    private dialog: MatDialog
  ) {
    // Check if template was not found (for published items)
    if (this.data.templateNotFound) {
      this.templateNotFound.set(true);
    }
  }

  /**
   * Replace {{variable}} placeholders with actual values
   * Handles optional whitespace inside braces: {{name}}, {{ name }}, {{  name  }}
   */
  private replaceVariables(content: string): string {
    if (!this.data.templateVariables?.length) return content;

    const langCode = this.selectedLang();

    for (const variable of this.data.templateVariables) {
      // Allow optional whitespace inside braces: {{ name }} or {{name}}
      const pattern = new RegExp(`\\{\\{\\s*${variable.name}\\s*\\}\\}`, 'g');
      const value = variable.value?.[langCode] ?? variable.value?.['en'] ?? `[${variable.name}]`;
      content = content.replace(pattern, String(value));
    }

    return content;
  }

  /** Check if there are variables available to show */
  hasVariables(): boolean {
    return !!this.data.templateVariables?.length;
  }

  closeDialog() {
    this.dialogRef.close();
  }

  toggleTheme() {
    this.isDarkMode.update(v => !v);
  }

  toggleVariableValues() {
    this.showVariableValues.update(v => !v);
  }

  setViewMode(mode: 'desktop' | 'mobile') {
    this.viewMode.set(mode);
  }

  toggleFullscreen() {
    this.isFullscreen.update(v => !v);
  }

  closeFullscreen() {
    this.isFullscreen.set(false);
  }

  updateDimensions(width: number, height: number) {
    this.customWidth.set(width);
    this.customHeight.set(height);
  }

  /** Open the test email dialog */
  openTestEmailDialog() {
    // If we have pre-computed language options, use them
    // Otherwise, build language options from available data
    const languages = this.data.languages || this.languageCodes().map(langCode => ({
      languageCode: langCode,
      languageName: getLanguageName(langCode),
      isValid: !!this.data.htmlPart?.[langCode],
      hasContent: !!this.data.htmlPart?.[langCode]
    }));

    // Source determines how test email is sent
    // Default to 'published' if not specified (backward compatible with published templates page)
    const source = this.data.source || 'published';

    this.dialog.open(TestEmailDialogComponent, {
      width: '500px',
      maxWidth: '90vw',
      data: {
        templateName: this.data.templateName,
        languages,
        templateVariables: this.data.templateVariables || [],
        defaultEmail: this.data.defaultEmail || '',
        defaultLangCode: this.data.defaultLangCode || 'en',
        source,
        // Only pass HTML content if source is 'editor'
        htmlPart: source === 'editor' ? this.data.htmlPart : undefined,
        subjectPart: source === 'editor' ? this.data.subjectPart : undefined,
      }
    });
  }
}
